import { memo, useRef, useMemo } from 'react';
import AssistantButton from './Button';
import AssistantDashboard from './Dashboard';
import { AssistantProvider } from './providers/Provider';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useMytaverseEvent } from '@core/dashboard/providers/MytaverseEventProvider';
import { useUserName } from './providers/UserName';
import { env } from 'env';

const { REACT_APP_ENABLE_ASSISTANT_ON_EVENTS_PAGE } = env;

const AssistantWrapper = memo(
  ({ isSelectEventPage }: { isSelectEventPage: boolean }) => {
    if (
      (REACT_APP_ENABLE_ASSISTANT_ON_EVENTS_PAGE === 'true' &&
        isSelectEventPage) ||
      !isSelectEventPage
    ) {
      return (
        <>
          <AssistantButton isLargeButton={isSelectEventPage} />
          <AssistantDashboard />
        </>
      );
    }

    return null;
  },
);

const Assistant = ({ isSelectEventPage }: { isSelectEventPage?: boolean }) => {
  const mytaverseEvent = useMytaverseEvent();
  const mytaverseEventRef = useRef(mytaverseEvent ?? {});
  mytaverseEventRef.current = mytaverseEvent ?? {};

  const { sendJSONMessageToWebSocket, user, selectedLanguage } = useMytaverse();
  const sendWSRef = useRef(sendJSONMessageToWebSocket);
  sendWSRef.current = sendJSONMessageToWebSocket;
  const mytaverseUserRef = useRef(user ?? {});

  const selectedLanguageRef = useRef(selectedLanguage);
  selectedLanguageRef.current = selectedLanguage;

  const userName = useUserName();

  const rawRegion = mytaverseEvent?.currentRegion?.region;
  const aiTriggerName = mytaverseEvent?.aiTriggerName;
  const objectPoiId = mytaverseEvent?.objectPoiId ?? '';
  // Sticky ref: only update when rawRegion is non-null so transient nulls
  // (e.g. from camera-sharing ParticipantPosition messages) don't change the
  // prop and break memo / trigger the region-change effect in the provider.
  const stickyRegionRef = useRef<string | undefined>(rawRegion);
  if (rawRegion) {
    stickyRegionRef.current = rawRegion;
  } else if (aiTriggerName) {
    stickyRegionRef.current = aiTriggerName;
  } else if (stickyRegionRef.current?.startsWith('AITrigger_')) {
    // aiTriggerName cleared — user left the AITrigger zone; reset sticky so
    // the provider's region-change effect fires and can reset the UI.
    stickyRegionRef.current = undefined;
  }
  const currentRegionName = stickyRegionRef.current;

  const children = useMemo(
    () => <AssistantWrapper isSelectEventPage={isSelectEventPage || false} />,
    [isSelectEventPage],
  );

  return (
    <AssistantProvider
      currentRegionName={currentRegionName}
      mytaverseUserRef={mytaverseUserRef}
      userName={userName}
      mytaverseEventRef={mytaverseEventRef}
      sendWSRef={sendWSRef}
      selectedLanguageRef={selectedLanguageRef}
      objectPoiId={objectPoiId}
    >
      {children}
    </AssistantProvider>
  );
};

export default Assistant;
